import { applySeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import { CONTACT, PROJECTS, LOCATIONS, HOME } from "../data/site";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { BrandMarquee } from "../components/site/BrandMarquee";
import { Testimonials } from "../components/site/Testimonials";
import { CTABand } from "../components/site/CTABand";

const Icon = ({ name, ...rest }) => { const C = Icons[name] || Icons.Check; return <C {...rest} />; };

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 900], [1, 1.12]);
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    applySeo({ paths: ["/"], fallbackTitle: "Cedar Views | Premium Aluminium Windows & Doors, Surrey & Hampshire" });
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-studies`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d) && d.length) setProjects(d); })
      .catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] overflow-hidden flex items-end">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={HOME.heroPoster}
            className="w-full h-full object-cover"
          >
            <source src="/videos/showroom-hero.webm" type="video/webm" />
            <source src="/videos/showroom-hero.mp4" type="video/mp4" />
            <source src="https://customer-assets-39nsmqrw.emergentagent.net/job_glazing-premium-ux/artifacts/q4wpjj7f_New%20Showroom.m4v" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/40 to-[#050A15]/20" />
        </motion.div>
        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <Reveal delay={0.1}>
            <p className="overline-x mb-6">{HOME.heroOverline}</p>
          </Reveal>
          <MaskedLines
            as="h1"
            lines={HOME.heroLines}
            delay={0.25}
            className="font-display tracking-tighter text-5xl sm:text-6xl lg:text-8xl text-white leading-[0.95]"
          />
          <Reveal delay={0.8} className="mt-8 max-w-xl">
            <h2 className="text-base md:text-lg text-white/70 font-normal leading-relaxed">
              {HOME.heroSub}
            </h2>
          </Reveal>
          <Reveal delay={0.95} className="mt-10 flex flex-wrap gap-4">
            <Link to={HOME.heroPrimary.to} data-testid="hero-cta-quote" className="btn-solid">
              {HOME.heroPrimary.label} <ArrowRight size={14} />
            </Link>
            <Link to={HOME.heroSecondary.to} data-testid="hero-cta-projects" className="btn-ghost">
              {HOME.heroSecondary.label}
            </Link>
          </Reveal>
        </div>
        <motion.div
          className="absolute bottom-8 right-8 hidden lg:block font-mono-x text-[10px] tracking-[0.3em] text-white/40 uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
        >
          Scroll — 01 / 06
        </motion.div>
      </section>

      {/* PRODUCT INDEX */}
      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <SectionHeading number="01" kicker={HOME.collectionKicker} title={HOME.collectionTitle} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {HOME.tiles.map((p, i) => (
              <Reveal key={p.to + i} delay={(i % 3) * 0.08} className={p.span}>
                <Link to={p.to} data-testid={`product-card${p.to.replaceAll("/", "-")}`} className={`group block relative overflow-hidden h-full min-h-[300px] ${p.h || "lg:min-h-[340px]"}`}>
                  <img src={p.img} alt={p.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A15]/90 via-[#050A15]/25 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-7 flex items-end justify-between">
                    <div>
                      {p.tag && <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/50">{p.tag}</p>}
                      <h3 className="font-display font-medium text-xl lg:text-2xl text-white mt-1.5">{p.label}</h3>
                      <p className="text-xs text-white/60 mt-1.5 max-w-xl">{p.d}</p>
                    </div>
                    <ArrowRight size={18} className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all shrink-0 mb-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <SectionHeading number="02" kicker={HOME.projectsKicker} title={HOME.projectsTitle} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {projects.slice(0, 3).map((pr, i) => (
            <Reveal key={pr.title} delay={i * 0.12}>
              <Link to={pr.has_full && pr.slug ? `/projects/${pr.slug}/` : "/projects/"} data-testid={`home-project-card-${i}`} className="group block">
                <div className="img-hover aspect-[4/5]">
                  <img src={pr.image} alt={pr.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/40 mt-6">{pr.location}</p>
                <h3 className="font-display font-medium text-xl text-white mt-2 group-hover:text-white/80 transition-colors">{pr.title}</h3>
                <p className="text-sm text-white/50 mt-2">{pr.spec}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <Link to="/projects/" data-testid="home-view-projects" className="btn-ghost">
            View the full portfolio <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      {/* NEWS: GLAZING STUDIO */}
      <section className="border-y hairline bg-white" data-testid="news-glazing-studio">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal className="img-hover aspect-[16/10]">
            <img
              src={HOME.newsImage || LOCATIONS[1]?.img}
              alt="The new Cedar Views Glazing Studio at Longacres Garden Centre, Bagshot"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <p className="overline-x" style={{ color: "rgba(30,42,54,0.55)" }}>{HOME.newsKicker}</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl mt-4" style={{ color: "#24333F" }}>
              {HOME.newsTitle}
            </h2>
            <p className="leading-relaxed mt-6 max-w-lg" style={{ color: "#3a4a58" }}>
              {HOME.newsText}
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to={HOME.newsButton.to} data-testid="news-book-appointment" className="btn-dark">
                {HOME.newsButton.label} <ArrowRight size={14} />
              </Link>
              <Link to="/showroom/" data-testid="news-showrooms-link" className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#24333F]/30 text-[#24333F] text-xs font-semibold tracking-[0.18em] uppercase hover:bg-[#24333F] hover:text-white transition-colors">
                Our showrooms
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <BrandMarquee />

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <SectionHeading number="03" kicker={HOME.manifestoKicker} title={HOME.manifestoTitle} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10">
          {HOME.manifesto.map((m, i) => (
            <Reveal key={m.t + i} delay={i * 0.12} className="bg-[#050A15] p-10 lg:p-12">
              <div className="flex items-center justify-between">
                <span className="font-mono-x text-xs text-[#3C64A8]">{String(i + 1).padStart(2, "0")}</span>
                <Icon name={m.icon} size={20} className="text-white/30" />
              </div>
              <h3 className="font-display font-medium text-2xl text-white mt-10 mb-4">{m.t}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{m.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Testimonials />

      {/* SHOWROOM STRIP */}
      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="img-hover aspect-[16/11] order-2 lg:order-1">
            <img src={HOME.showroomImage} alt="Inside the Cedar Views showroom — Sunflex and Origin displays" loading="lazy" className="w-full h-full object-cover" />
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHeading number="05" kicker={HOME.showroomKicker} title={HOME.showroomTitle} />
            <Reveal delay={0.1}>
              <p className="text-white/60 leading-relaxed max-w-lg -mt-8">
                {HOME.showroomText}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/showroom/" data-testid="home-showroom-link" className="btn-solid">
                  Plan your visit <ArrowRight size={14} />
                </Link>
                <a href={CONTACT.phoneHref} className="btn-ghost">
                  <Phone size={14} /> {CONTACT.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
